import { ElMessage } from "element-plus";
/**
 * 判断是否是微信浏览器
 * @returns {Boolean}
 */
export const isWxBrowser = () => {
  // 判断是否H5微信环境，true为微信浏览器
  const ua = navigator.userAgent.toLowerCase();
  return ua.match(/MicroMessenger/i) == "micromessenger" ? true : false;
};

/**
 * 下载工具
 * @param {string} url 下载地址
 * @param {string} name 文件名
 *
 */
export const downloadTool = (url, name = "") => {
  if (isWxBrowser()) {
    ElMessage({
      message: "请先在浏览器中打开再下载！",
      type: "error"
    });
  } else {
    let a = document.createElement("a");
    a.style = "display: none"; // 创建一个隐藏的a标签
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click(); // 触发a标签的click事件
    document.body.removeChild(a);
  }
};

/**
 * 下载工具
 * @param {string} url 下载地址
 * @param {string} name 文件名
 *
 */
export const downloadFile = async (url, filename = "") => {
  if (isWxBrowser()) {
    ElMessage({
      message: "请先在浏览器中打开再下载！",
      type: "error"
    });
  } else {
    try {
      // 如果是同源文件，直接使用 download 属性
      if (new URL(url).origin === window.location.origin) {
        const a = document.createElement("a");
        a.style = "display: none"; // 创建一个隐藏的a标签
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click(); // 触发a标签的click事件
        document.body.removeChild(a);
      } else {
        // 跨域文件使用 fetch
        const response = await fetch(url);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.style = "display: none"; // 创建一个隐藏的a标签
        a.href = objectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click(); // 触发a标签的click事件
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
      }
    } catch (error) {
      console.error("下载失败:", error);
    }
  }
};

/**
 * 复制文本到粘贴板
 * 使用：import { copyTxt } from "@/utils/tools.js";
 *       copyTxt(`${copyUrl}/#/editform?password=${data.encodedCode}`);
 * @param {String} txt 需要粘贴的文本
 * @returns {Function}
 *
 */
export let copyTxt = (txt) => {
  return new Promise((resolve, reject) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(txt)
        .then(() => resolve())
        .catch(() => {
          fallbackCopyText(txt, resolve, reject);
        });
    } else {
      fallbackCopyText(txt, resolve, reject);
    }
  });
};

function fallbackCopyText(text, resolve, reject) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "-9999px";
  textarea.readOnly = true;
  document.body.appendChild(textarea);
  textarea.select();
  textarea.setSelectionRange(0, textarea.value.length);

  try {
    const success = document.execCommand("copy");
    if (success) {
      resolve();
    } else {
      reject(new Error("复制失败"));
    }
  } catch (err) {
    reject(err);
  } finally {
    document.body.removeChild(textarea);
  }
}

/**
 * 复制表格数据到剪贴板
 * 支持粘贴到Excel后保持格式
 * @param {Array} data - 表格数据数组
 * @param {Array} columns - 列配置数组
 * @param {Object} options - 可选配置
 * @param {boolean} options.includeHeader - 是否包含表头，默认true
 * @param {string} options.delimiter - 列分隔符，默认"\t"
 * @param {string} options.lineBreak - 行分隔符，默认"\r\n"
 * @param {Function} options.onSuccess - 复制成功回调函数
 * @param {Function} options.onError - 复制失败回调函数
 * @returns {boolean} - 是否复制成功
 */
export function copyTableToClipboard(data, columns, options = {}) {
  const {
    includeHeader = true,
    delimiter = "\t",
    lineBreak = "\r\n",
    onSuccess,
    onError
  } = options;

  try {
    const rows = [];

    if (includeHeader && columns?.length) {
      const headerRow = columns.map((col) => col.label || col.prop || "").join(delimiter);
      rows.push(headerRow);
    }
    if (data?.length) {
      data.forEach((row, rowIndex) => {
        const rowValues = columns.map((col) => {
          let value = "";

          if (col.getValue) {
            value = col.getValue(row, rowIndex);
          } else if (col.prop) {
            const props = col.prop.split(".");
            value = props.reduce((obj, prop) => obj?.[prop], row);
          }
          return value != null ? String(value) : "";
        });
        rows.push(rowValues.join(delimiter));
      });
    }

    const tableText = rows.join(lineBreak);

    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(tableText)
        .then(() => {
          onSuccess?.();
        })
        .catch((err) => {
          fallbackCopy(tableText, onSuccess, onError);
        });
    } else {
      fallbackCopy(tableText, onSuccess, onError);
    }

    return true;
  } catch (err) {
    onError?.(err);
    return false;
  }
}

function fallbackCopy(text, onSuccess, onError) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();

  try {
    const success = document.execCommand("copy");
    if (success) {
      onSuccess?.();
    } else {
      onError?.(new Error("复制失败"));
    }
  } catch (err) {
    onError?.(err);
  } finally {
    document.body.removeChild(textarea);
  }
}

/**
 * 快速生成UUID
 * @returns 唯一ID
 */
export function generateFastUUID() {
  const hex = "0123456789abcdef";
  let uuid = "";

  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      uuid += "-";
    } else if (i === 14) {
      uuid += "4"; // UUID version 4
    } else if (i === 19) {
      uuid += hex[(Math.random() * 4) | 8]; // UUID variant
    } else {
      uuid += hex[(Math.random() * 16) | 0];
    }
  }

  return uuid;
}

/**
 * 仅替换URL地址最后一个/之后的内容，会保留URL后面携带的参数（如果有的话）
 * @param {String} url 需要处理的URL地址
 * @param {String} newSegment 替换值
 * @returns 处理后的新URL
 */
export function replaceLastPathSegment(url, newSegment) {
  // 匹配最后一个/到字符串末尾，或到?或#之前
  return url.replace(/\/[^/?#]*([?#].*)?$/, `/${newSegment}$1`);
}

/**
 * 安全检查对象是否具有某个属性
 * @param {Object} obj - 要检查的对象
 * @param {string} prop - 属性名
 * @returns {boolean} 是否具有该属性
 */
function hasOwnProperty(obj, prop) {
  return Object.prototype.hasOwnProperty.call(obj, prop);
}

/**
 * 将带单位的字符串转换为字节数
 * @param {string} sizeStr - 带单位的字符串
 * @returns {number} 字节数
 */
function convertToBytes(sizeStr) {
  // 完整的单位映射表
  const units = {
    B: 1,
    K: 1024,
    KB: 1024,
    M: 1024 ** 2,
    MB: 1024 ** 2,
    G: 1024 ** 3,
    GB: 1024 ** 3,
    T: 1024 ** 4,
    TB: 1024 ** 4,
    P: 1024 ** 5,
    PB: 1024 ** 5
  };

  // 默认单位为G
  const DEFAULT_UNIT = "G";

  if (!sizeStr || typeof sizeStr !== "string") {
    return 0;
  }

  // 清理字符串并转为大写
  const cleanStr = sizeStr.trim().toUpperCase();

  // 提取数字部分（支持小数点和负数）
  const numMatch = cleanStr.match(/[-+]?(\d+(\.\d*)?|\.\d+)/);
  if (!numMatch) {
    // 如果没有找到数字，返回0
    return 0;
  }

  const num = parseFloat(numMatch[0]);

  // 提取单位部分（字母字符）
  const unitMatch = cleanStr.match(/[A-Z]+/g);
  let foundUnit = null;

  if (unitMatch) {
    // 从后往前查找有效的单位
    for (let i = unitMatch.length - 1; i >= 0; i--) {
      const potentialUnit = unitMatch[i];
      if (hasOwnProperty(units, potentialUnit)) {
        foundUnit = potentialUnit;
        break;
      }
    }
  }

  // 使用找到的单位或默认单位
  const unit = foundUnit || DEFAULT_UNIT;

  if (hasOwnProperty(units, unit)) {
    return num * units[unit];
  }

  // 如果单位不存在，使用默认单位
  return num * units[DEFAULT_UNIT];
}

/**
 * 将字节数转换为指定单位的值
 * @param {number} bytes - 字节数
 * @param {string} targetUnit - 目标单位 ('B', 'K', 'M', 'G', 'T', 'P')
 * @returns {number} 转换后的值
 */
function convertToUnit(bytes, targetUnit = "G") {
  const unitMap = {
    B: 1,
    K: 1024,
    KB: 1024,
    M: 1024 ** 2,
    MB: 1024 ** 2,
    G: 1024 ** 3,
    GB: 1024 ** 3,
    T: 1024 ** 4,
    TB: 1024 ** 4,
    P: 1024 ** 5,
    PB: 1024 ** 5
  };

  const unit = targetUnit.toUpperCase();

  if (!hasOwnProperty(unitMap, unit)) {
    throw new Error(`不支持的单位: ${targetUnit}`);
  }

  return bytes / unitMap[unit];
}

/**
 * 格式化数字，始终输出为G单位
 * @param {number} value - 数值
 * @param {number} precision - 小数位数
 * @returns {string} 格式化后的字符串
 */
function formatAsG(value, precision = 3, unit = "G") {
  if (typeof value !== "number" || isNaN(value)) {
    return "0" + unit;
  }

  // 格式化数字，保留指定位数小数
  let formattedValue;
  if (Math.abs(value) < 0.001 && value !== 0) {
    // 处理非常小的数值
    formattedValue = value.toExponential(precision - 1);
  } else {
    formattedValue = value.toFixed(precision);
    // 移除末尾的0
    formattedValue = formattedValue.replace(/(\.\d*?)0+$/, "$1");
    formattedValue = formattedValue.replace(/\.$/, "");
  }

  // 始终添加G单位
  return formattedValue + unit;
}

/**
 * 计算两个带单位字符串的差值，结果始终以G为单位
 * @param {string} aStr - 第一个字符串
 * @param {string} bStr - 第二个字符串
 * @param {Object} options - 配置选项
 * @returns {string|Object} 格式化后的差值或详细结果对象
 */
function subtractSizes(aStr, bStr, options = {}) {
  const defaultOptions = {
    returnObject: false,
    precision: 3,
    unit: "G" // 固定输出单位为G
  };

  const config = { ...defaultOptions, ...options };
  const { returnObject, precision, unit } = config;

  // 转换为字节
  const aBytes = convertToBytes(aStr);
  const bBytes = convertToBytes(bStr);

  // 计算差值（字节）
  const diffBytes = aBytes - bBytes;

  // 转换为G单位
  const diffG = convertToUnit(diffBytes, "G");

  // 格式化结果为G单位
  const formattedDiff = formatAsG(diffG, precision, unit);

  if (returnObject) {
    // 所有值都转换为G单位
    const aG = convertToUnit(aBytes, "G");
    const bG = convertToUnit(bBytes, "G");

    return {
      difference: formattedDiff,
      differenceG: diffG,
      differenceBytes: diffBytes,
      aG: formatAsG(aG, precision),
      bG: formatAsG(bG, precision),
      aBytes,
      bBytes,
      aValue: aStr,
      bValue: bStr,
      aGValue: aG,
      bGValue: bG,
      precision,
      unit: "G"
    };
  }

  return formattedDiff;
}

/**
 * 简化的减法函数（主要接口）- 始终返回G单位
 * @param {string} a - 第一个字符串
 * @param {string} b - 第二个字符串
 * @param {number} precision - 小数位数，默认3位
 * @returns {string} 格式化后的差值（G单位）
 */
export function sizeSubtract(a, b, options = { precision: 3, unit: "G" }) {
  const { precision, unit } = options;
  return subtractSizes(a, b, { precision, returnObject: false, unit });
}

/**
 * 批量计算多个值的差值
 * @param {...string} sizes - 多个带单位的字符串
 * @returns {string} 所有值的差值（G单位）
 */
export function subtractMultipleSizes(...sizes) {
  if (sizes.length === 0) {
    return "0G";
  }

  let totalBytes = convertToBytes(sizes[0]);

  for (let i = 1; i < sizes.length; i++) {
    totalBytes -= convertToBytes(sizes[i]);
  }

  const totalG = convertToUnit(totalBytes, "G");
  return formatAsG(totalG);
}
